import React, { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toastError, toastSuccess } from "./ToastNotification";
import UserService from "../services/user/user";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Camera, Pencil } from "lucide-react";
import Loader from "../assets/images/loader.gif";
import User from "../assets/images/user.png";

const ModalPopUp = ({ type, getAllUser, data }) => {
  console.log("🚀 ~ ModalPopUp ~ data:", data);
  const [showPassword, setShowPassword] = useState(false);
  const [open, setOpen] = useState(false); // Manage modal state
  const [userProfile, setUserProfile] = useState("");
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(User);
  const [isLoading, setIsLoading] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (values) => {
    console.log("das a dabd");
    setIsLoading(true);
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("email", values.email);
    formData.append("mobileNumber", values.mobileNumber);

    if (userProfile) {
      formData.append("profile", userProfile);
    }

    if (type === "add") {
      // formData.append("userType", values.userType);
      formData.append("password", values.password);
    }

    if (type === "add") {
      try {
        const response = await UserService?.createUser(formData);
        if (response) {
          toastSuccess(response?.data?.message);
          getAllUser(false);
          setOpen(false); // Close the modal after successful submission
          formik.resetForm();
          setUserProfile("");
          setImage(User);
        }
      } catch (error) {
        toastError(error?.response?.data?.message);
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        const response = await UserService?.updateUser(data._id, formData);
        if (response) {
          toastSuccess(response?.data?.message);
          getAllUser(false);
          setOpen(false); // Close the modal after successful submission
          formik.resetForm();
          setUserProfile("");
          setImage(User);
        }
      } catch (error) {
        toastError(error?.response?.data?.message);
      } finally {
        setIsLoading(false);
      }
      // Handle update logic here
    }
  };

  // Prevent form from submitting on Enter
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobileNumber: "",
      password: "",
      // userType: "",
      profile: userProfile,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      mobileNumber: Yup.string()
        .matches(/^\d+$/, "Must be a valid mobile number")
        .required("Number is required"),
      password: type === "add" && Yup.string().required("Password is required"),
      // userType:
      //   type === "add" && Yup.string().required("User type is required"), // Validation for user type
    }),
    onSubmit: handleSubmit,
  });

  // Reset form when the modal is closed or opened
  const handleModalClose = () => {
    formik.resetForm();
    setOpen(false);
    setImage("");
    setUserProfile("");
  };

  const handleModalOpen = async (type) => {
    setOpen(true);
    if (type === "add") {
      setImage(User);
      formik.resetForm();
    } else if (type === "edit") {
      // const response = await UserService.getUserDetails(data?._id);
      // const result = response.data.data;
      setImage(data.profile || User);
      formik.setFieldValue("email", data.email);
      formik.setFieldValue("mobileNumber", data.mobileNumber);
      formik.setFieldValue("name", data.name);
      formik.setFieldValue("profile", data.profile);
    }
  };

  const handleTypChange = (e) => {
    formik.setFieldValue("userType", e);
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUserProfile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) =>
        isOpen ? handleModalOpen() : handleModalClose()
      }
    >
      <DialogTrigger asChild>
        {type === "add" ? (
          <Button
            className="bg-primary hover:bg-btnHover text-white text-base"
            onClick={() => handleModalOpen("add")}
          >
            + Add New
          </Button>
        ) : (
          <div
            className="group border rounded-md p-1 border-[#7064E9] hover:bg-[#7064E9] duration-200 cursor-pointer"
            onClick={() => handleModalOpen("edit")}
          >
            <Pencil className="w-4 h-4 text-[#7064E9] group-hover:text-white" />
          </div>
        )}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-[500px] max-w-[calc(100%-40px)] mx-auto max-h-[calc(100vh-10vh)] overflow-y-auto custom-scroll"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle className="text-white">Add User</DialogTitle>
        </DialogHeader>
        <div>
          <form onSubmit={formik.handleSubmit} className="grid gap-4 py-4">
            <div className="flex justify-center">
              <div className="relative">
                <div className="h-20 w-20 rounded-full overflow-hidden border">
                  <img
                    src={image}
                    alt="User"
                    className="object-cover bg-center w-full h-full"
                  />
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/png, image/jpeg"
                  onChange={handleImageChange}
                />
                <div
                  className="cursor-pointer absolute bottom-0 right-0 bg-secondary border rounded-full p-1"
                  onClick={handleImageClick}
                >
                  <Camera className="h-4 w-4" />
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/png, image/jpeg"
                onChange={handleImageChange}
              />
            </div>
            <div className="grid items-center gap-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                placeholder="Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoFocus={false}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="text-red-500">{formik.errors.name}</div>
              )}
            </div>
            <div className="grid items-center gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                name="email"
                placeholder="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-red-500">{formik.errors.email}</div>
              )}
            </div>
            <div className="grid items-center gap-1.5">
              <Label htmlFor="mobileNumber">Phone Number</Label>
              <Input
                id="mobileNumber"
                name="mobileNumber"
                type="text" // Change to text to allow leading zeros
                placeholder="Phone Number"
                value={formik.values.mobileNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.mobileNumber && formik.errors.mobileNumber && (
                <div className="text-red-500">{formik.errors.mobileNumber}</div>
              )}
            </div>
            {type === "add" && (
              <>
                {/* <div className="grid items-center gap-1.5">
                  <Label htmlFor="userType">User Type</Label>
                  <Select onValueChange={handleTypChange}>
                    <SelectTrigger
                      name="userType"
                      id="userType"
                      value={formik.values.userType}
                      onBlur={formik.handleBlur}
                      className="w-full"
                      tabIndex="-1"
                    >
                      <SelectValue placeholder="Select a user type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="Individual">Individual</SelectItem>
                        <SelectItem value="Organization">
                          Organization
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {formik.touched.userType && formik.errors.userType && (
                    <div className="text-red-500">{formik.errors.userType}</div>
                  )}
                </div> */}
                <div className="grid items-center gap-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder="Password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <div
                      className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? (
                        <FaEyeSlash className="text-white/40" />
                      ) : (
                        <FaEye className="text-white/40" />
                      )}
                    </div>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <div className="text-red-500">{formik.errors.password}</div>
                  )}
                </div>
              </>
            )}
            <DialogFooter className="gap-3">
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full bg-transparent border text-primary border-primary hover:bg-primary hover:text-white"
                  onClick={handleModalClose}
                >
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type="submit"
                variant="destructive"
                className="w-full bg-primary hover:bg-btnHover text-white hover:text-white text-base border-0"
              >
                {isLoading ? (
                  <img src={Loader} alt="Loader" className="w-7" />
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ModalPopUp;
